/* ============================================================
   Karigur Bridal — Lightweight motion v3 (kb-motion.js)
   - No external dependencies.
   - Adds .kb-js to <html> so reveal CSS only activates with JS.
   - Reveals [data-kb-reveal] EARLY: an IntersectionObserver pre-reveals each
     block ~35% of a viewport BEFORE it scrolls into view, so content is already
     painted (never blank/faint) by the time it reaches the fold — even on a
     fast flick.
   - Belt-and-suspenders: a throttled scroll/resize sweep reveals anything within
     ~1.3 viewports, guaranteeing nothing is ever stranded if an observer
     callback is missed during rapid scrolling. It detaches once all are shown.
   - Hard failsafes at 0.8s (near view) and 5s (everything).
   - Respects prefers-reduced-motion (content shown immediately).
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('kb-js');

  var prefersReduced =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function reveal(el, instant) {
    // `instant` snaps the block in with no fade — used when a jump/flick lands
    // with the block already in view, so it's never caught mid-transition.
    if (instant) el.classList.add('kb-instant');
    el.classList.add('is-visible');
  }
  function revealAll(items, instant) { for (var i = 0; i < items.length; i++) reveal(items[i], instant); }

  function init() {
    var items = document.querySelectorAll('[data-kb-reveal]');
    if (!items.length) return;

    if (prefersReduced || !('IntersectionObserver' in window)) {
      revealAll(items, true);
      return;
    }

    var pending = [];
    for (var i = 0; i < items.length; i++) pending.push(items[i]);

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          // Pre-revealed before the fold -> let it fade up as the user approaches.
          // Already on screen (initial paint / jump) -> snap so it's never mid-fade.
          var r0 = entry.target.getBoundingClientRect();
          reveal(entry.target, r0.top < (window.innerHeight || 800) * 0.85 && r0.bottom > 0);
          observer.unobserve(entry.target);
        });
      },
      // Pre-reveal ~35% of a viewport early so content is painted before it
      // reaches the fold (was -4%, which revealed only once already in view).
      { rootMargin: '0px 0px 35% 0px', threshold: 0 }
    );

    pending.forEach(function (el) { observer.observe(el); });

    // Throttled scroll/resize sweep: reveal anything within ~1.3 viewports.
    // This is the guarantee that a fast flick can never leave a block blank.
    var ticking = false;
    function sweep() {
      ticking = false;
      var vh = window.innerHeight || 800;
      var still = [];
      for (var i = 0; i < pending.length; i++) {
        var el = pending[i];
        if (el.classList.contains('is-visible')) continue;
        var r = el.getBoundingClientRect();
        if (r.top < vh * 1.3 && r.bottom > -vh * 0.3) {
          // Already substantially in view (a jump/flick landed on it) -> snap; otherwise fade up.
          reveal(el, r.top < vh * 0.85);
          observer.unobserve(el);
        } else {
          still.push(el);
        }
      }
      pending = still;
      if (!pending.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(sweep); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    sweep(); // initial pass for above-fold + near-fold blocks

    // Hard failsafes: content can never be lost to a missed observer callback.
    setTimeout(sweep, 800);
    setTimeout(function () { revealAll(items, true); }, 5000);
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  } catch (e) {
    revealAll(document.querySelectorAll('[data-kb-reveal]'), true);
  }

  // Theme editor: reveal newly loaded sections so [data-kb-reveal] content is
  // never stuck hidden when a section is added or reordered in the editor.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var scope = (e && e.target) || document;
      revealAll(scope.querySelectorAll('[data-kb-reveal]'), true);
    });
  }
})();
